/* Le parcours d'un dossier de packaging, et le stock qu'il laisse derrière lui.
 *
 * Cinq étapes, et chacune attend une donnée précise — pas un bouton. Sans coût
 * envoyé il n'y a rien à designer, sans devis rien à faire valider, sans
 * facture réglée rien à lancer. Le franchissement se refuse donc tout seul, et
 * l'écran n'a qu'à dire ce qui manque.
 *
 * Le stock, lui, ne se stocke pas : ce qui reste d'un dossier est ce qu'on a
 * reçu moins ce qu'on a vendu depuis. Tenir un compteur à côté, c'est se
 * donner deux chiffres qui finiront par diverger sans qu'on sache lequel
 * croire. On le recalcule, et il est juste par construction.
 */

import type { DossierPackaging, EtapePackaging, LigneVente, Vente } from "@/types/metier";

export interface DefEtapePackaging {
  cle: EtapePackaging;
  nom: string;
  court: string;
  ton: string;
  /** Ce que l'étape raconte, en une ligne, sur la fiche. */
  quoi: string;
}

export const ETAPES: readonly DefEtapePackaging[] = [
  { cle: "technique", nom: "Infos techniques", court: "Technique", ton: "ink-3",
    quoi: "Les contraintes techniques et le coût par packaging sont partis au client." },
  { cle: "design", nom: "Design à l'usine", court: "Design", ton: "accent",
    quoi: "Le design du client est chez l'usine, qui doit le deviser." },
  { cle: "validation", nom: "Validation client", court: "Validation", ton: "violet",
    quoi: "Design et devis sont chez le client, qui doit donner son accord." },
  { cle: "production", nom: "En production", court: "Production", ton: "warn",
    quoi: "La facture est réglée, la fabrication est lancée." },
  { cle: "recu", nom: "Reçu", court: "Reçu", ton: "ok",
    quoi: "Les packagings sont arrivés : ils alimentent le stock du client." },
];

export const ABANDONNE: DefEtapePackaging = {
  cle: "abandonne", nom: "Abandonné", court: "Abandonné", ton: "crit",
  quoi: "Le dossier ne se poursuit pas.",
};

export function etapeDe(cle: string | null | undefined): DefEtapePackaging {
  if (cle === "abandonne") return ABANDONNE;
  return ETAPES.find((e) => e.cle === cle)
    ?? { cle: (cle ?? "technique") as EtapePackaging, nom: cle || "—", court: cle || "—",
         ton: "", quoi: "" };
}

/** Un dossier reçu ou abandonné ne compte plus dans les dossiers ouverts. */
export const CLOS: readonly string[] = ["recu", "abandonne"];

export function ouvert(d: Pick<DossierPackaging, "etape">): boolean {
  return !CLOS.includes(d.etape);
}

export function etapeSuivante(cle: string): EtapePackaging | null {
  if (cle === "abandonne") return null;
  const i = ETAPES.findIndex((e) => e.cle === cle);
  if (i < 0 || i >= ETAPES.length - 1) return null;
  return ETAPES[i + 1]!.cle;
}

/* ──────────────────────────────── les portes ───────────────────────────────
   Ce qu'il faut avoir avant de passer à l'étape suivante. La clé est l'étape
   QUITTÉE : on demande ce qui prouve qu'elle est faite. */

interface Porte {
  /** Ce qui manque, dit au client de l'écran. */
  manque: string;
  tenu: (d: DossierPackaging) => boolean;
}

const PORTES: Partial<Record<EtapePackaging, Porte>> = {
  technique: {
    manque: "Renseignez le coût par packaging : c'est ce qu'on annonce au client.",
    tenu: (d) => d.cout_unitaire != null,
  },
  design: {
    manque: "Renseignez le devis de l'usine avant de renvoyer le dossier au client.",
    tenu: (d) => d.devis_montant != null,
  },
  validation: {
    manque: "Il faut l'accord du client, puis la facture packaging réglée, pour lancer.",
    tenu: (d) => d.valide_le != null && d.paye_le != null,
  },
  production: {
    manque: "Renseignez la quantité reçue : c'est elle qui alimente le stock.",
    tenu: (d) => d.quantite_recue != null,
  },
};

/** Ce qui empêche d'avancer, ou rien. */
export function blocage(d: DossierPackaging): string | null {
  if (d.etape === "abandonne") return "Ce dossier est abandonné.";
  if (!etapeSuivante(d.etape)) return null;
  const p = PORTES[d.etape as EtapePackaging];
  if (!p) return null;
  return p.tenu(d) ? null : p.manque;
}

/* ──────────────────────────────── le stock ─────────────────────────────────
   Ce qui reste d'un dossier reçu : sa quantité, moins ce que les commandes
   ont consommé depuis. Plusieurs dossiers sur la même référence et le même
   client s'épuisent dans l'ordre d'arrivée — le plus ancien d'abord, comme
   un stock réel. */

/** Les étapes de vente où la marchandise est engagée, donc le packaging pris. */
const ENGAGEES = new Set(["commande_client", "commande_usine", "expedie", "livre",
                          "facture", "paye"]);

export interface Consommation {
  /** Les quantités vendues, par (client, produit), après une date donnée. */
  qte: number;
  depuis: string;
}

export interface Stock {
  dossier: DossierPackaging;
  recu: number;
  consomme: number;
  restant: number;
}

/**
 * Ce que chaque dossier reçu a encore en stock.
 *
 * `ventes` et `lignes` sont ceux qu'on a déjà sous la main : on ne rappelle
 * pas la base pour un calcul qui se fait sur ce qui est chargé.
 */
export function stocks(
  dossiers: readonly DossierPackaging[],
  ventes: readonly Vente[],
  lignes: readonly (LigneVente & { vente?: Vente | null })[],
): Stock[] {
  const venteDe = new Map(ventes.map((v) => [v.id, v] as const));

  /* Les sorties, par couple client + référence, avec leur date. On les trie
     pour pouvoir servir les dossiers dans l'ordre d'arrivée. */
  const sorties = new Map<string, { date: string; qte: number }[]>();
  for (const l of lignes) {
    const v = l.vente ?? venteDe.get(l.vente_id);
    if (!v || !v.client_id || !l.produit_id) continue;
    if (!ENGAGEES.has(v.etape)) continue;
    const cle = `${v.client_id}|${l.produit_id}`;
    const liste = sorties.get(cle) ?? [];
    liste.push({ date: v.date_document, qte: Number(l.qte) });
    sorties.set(cle, liste);
  }
  for (const liste of sorties.values()) liste.sort((a, b) => a.date.localeCompare(b.date));

  const recus = dossiers
    .filter((d) => d.recu_le && d.client_id && d.produit_id && d.quantite_recue != null)
    .sort((a, b) => (a.recu_le ?? "").localeCompare(b.recu_le ?? ""));

  /* Chaque sortie se sert sur le plus ancien dossier déjà reçu à sa date. Ce
     qu'aucun dossier ne couvre n'est pas une erreur : c'est vendu avant
     d'avoir eu du packaging, et ça ne doit pas créer de stock négatif
     ailleurs. */
  const restant = new Map<string, number>();
  for (const d of recus) restant.set(d.id, Number(d.quantite_recue));

  for (const [cle, liste] of sorties) {
    const [clientId, produitId] = cle.split("|");
    const miens = recus.filter((d) => d.client_id === clientId && d.produit_id === produitId);
    if (!miens.length) continue;
    for (const s of liste) {
      let reste = s.qte;
      for (const d of miens) {
        if (reste <= 0) break;
        if ((d.recu_le ?? "") > s.date) continue;
        const dispo = restant.get(d.id) ?? 0;
        if (dispo <= 0) continue;
        const pris = Math.min(dispo, reste);
        restant.set(d.id, dispo - pris);
        reste -= pris;
      }
    }
  }

  return recus.map((d) => {
    const recu = Number(d.quantite_recue);
    const r = restant.get(d.id) ?? 0;
    return { dossier: d, recu, consomme: recu - r, restant: r };
  });
}

/** Ce qu'un client a encore de packaging, référence par référence. */
export interface StockClient {
  produit_id: string;
  usine_id: string | null;
  recu: number;
  restant: number;
}

export function stocksDuClient(liste: readonly Stock[], clientId: string): StockClient[] {
  const m = new Map<string, StockClient>();
  for (const s of liste) {
    if (s.dossier.client_id !== clientId || !s.dossier.produit_id) continue;
    const e = m.get(s.dossier.produit_id) ?? {
      produit_id: s.dossier.produit_id, usine_id: s.dossier.usine_id, recu: 0, restant: 0,
    };
    e.recu += s.recu;
    e.restant += s.restant;
    m.set(s.dossier.produit_id, e);
  }
  return [...m.values()].sort((a, b) => b.restant - a.restant);
}

/** Sous ce seuil de couverture, le stock mérite qu'on le regarde. */
export const SEUIL_ALERTE = 0.15;

/** Un stock presque épuisé : il reste moins de 15 % de ce qui est arrivé. */
export function faible(s: Stock): boolean {
  return s.recu > 0 && s.restant / s.recu < SEUIL_ALERTE;
}
